/**
 * ImageSelectPrint - Figma-matching print renderer for image-select quizzes
 * Layout: PrintQuizTitle → image with overlay regions (same as before, redesigned styling)
 */

import { cn } from '../../../lib/utils'
import { KatexText } from '../shared'
import type { ImageSelectQuizData } from '../../../types/legacyQuiz'
import { PrintQuizTitle, PrintQuizProblem, getStyles, getImageUrl, getPrintScale } from './print-common'

interface PrintQuizProps {
  quizId: string | number
  quiz: ImageSelectQuizData
  number: number
  showCorrectAnswers?: boolean
  correctAnswers?: string[]
  compact?: boolean
}

export function ImageSelectPrint({
  quizId,
  quiz,
  number,
  showCorrectAnswers = false,
  correctAnswers,
  compact,
}: PrintQuizProps) {
  const s = getStyles(compact)
  const imageUrl = getImageUrl(quiz.image, quiz.imagePrint)
  const printScale = getPrintScale(quiz.image, quiz.imagePrint)
  const correctKeySet = new Set(correctAnswers ?? [])

  return (
    <div className={cn(s.container, s.quizSection)}>
      {/* Title */}
      <PrintQuizTitle number={number} compact={compact}>
        <KatexText text={quiz.title} />
      </PrintQuizTitle>

      {/* Problem body (with {image=N} → additionalImages) */}
      <PrintQuizProblem
        problem={quiz.problem}
        quizId={quizId}
        additionalImages={quiz.additionalImages}
        compact={compact}
      />

      {/* Image with overlay regions */}
      {imageUrl && (
        <div className="flex justify-center w-full">
          <div
            className="relative inline-block origin-center"
            style={{
              transform: `scale(${printScale ?? 0.75})`,
            }}
          >
            <img
              src={imageUrl}
              alt="quiz"
              className="max-w-full h-auto"
            />

            {/* Overlay numbered labels on each selectable region */}
            {quiz.choices.map((rect, idx) => {
              const isCorrect = showCorrectAnswers && correctKeySet.has(rect.key)

              const x = Math.max(0, rect.x)
              const y = Math.max(0, rect.y)
              const width = rect.x + rect.width > 100 ? 100 - rect.x : rect.width
              const height = rect.y + rect.height > 100 ? 100 - rect.y : rect.height

              return (
                <div
                  key={rect.key}
                  className="absolute"
                  style={{
                    left: `${x}%`,
                    top: `${y}%`,
                    width: `${width}%`,
                    height: `${height}%`,
                  }}
                >
                  <span
                    className={cn(
                      'absolute -top-[8px] -left-[8px] w-[20px] h-[20px] rounded-full flex items-center justify-center text-[11px] font-bold',
                      isCorrect
                        ? 'bg-[#e50000] text-white'
                        : 'bg-white text-[#999] border border-[#ccc]'
                    )}
                  >
                    {idx + 1}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
